/**
 * KPIs de catálogo — conteos globales desde el snapshot RAM (ADR-017).
 * Independientes de filtros/paginación del listado.
 */
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import {
  loadWebCatalogSnapshotRows,
  aggregateRemoteStocksByProduct,
  type RemoteSnapshotStockRow,
} from './productCatalogRemoteSnapshot';
import { getCatalogProductStockSignal } from '../domain/catalogProductStockSignal';
import { getProductCompletenessIssues } from '../domain/catalogProductCompleteness';
import { mapRemoteSnapshotToCatalogProductRow } from './catalogReadRepo';

export type CatalogKpiSnapshot = {
  totalProducts: number;
  /** Activos visibles en menú/tienda: no pausados (menu_available) ni privados (is_public). */
  onMenuCount: number;
  /** Ítems tipo servicio activos. */
  servicesCount: number;
  alertsCount: number;
  incompleteCount: number;
};

const EMPTY: CatalogKpiSnapshot = {
  totalProducts: 0,
  onMenuCount: 0,
  servicesCount: 0,
  alertsCount: 0,
  incompleteCount: 0,
};

async function computeKpisFromRemoteSnapshot(
  branchId: string | null,
  options?: { includeStockAlerts?: boolean },
): Promise<CatalogKpiSnapshot> {
  const includeStockAlerts = options?.includeStockAlerts !== false;
  const snapshot = await loadWebCatalogSnapshotRows();
  if (!snapshot) return { ...EMPTY };

  const stockByProduct = branchId
    ? aggregateRemoteStocksByProduct(branchId, snapshot.stocks, { weightedAvgCost: true })
    : new Map<string, RemoteSnapshotStockRow>();

  const rows = snapshot.products.map((row) =>
    mapRemoteSnapshotToCatalogProductRow(row, branchId ? stockByProduct.get(row.id) : undefined),
  );

  const active = rows.filter((r) => r.isActive && !r.isMisc);
  let alertsCount = 0;
  let incompleteCount = 0;

  for (const product of active) {
    if (getProductCompletenessIssues(product).length > 0) incompleteCount++;
    if (!includeStockAlerts || product.itemType === 'service') continue;
    const { tone } = getCatalogProductStockSignal(product);
    if (tone === 'warning' || tone === 'danger') alertsCount++;
  }

  return {
    totalProducts: active.length,
    // «En menú» = visible para el cliente: activo + no pausado (menu_available)
    // + público (is_public). Sin is_public el tile contaba ítems ocultos de la
    // tienda y tendía a igualar el total — métrica sin información.
    onMenuCount: active.filter((r) => r.menuAvailable !== false && r.isPublic !== false).length,
    servicesCount: active.filter((r) => r.itemType === 'service').length,
    alertsCount,
    incompleteCount,
  };
}

export async function fetchCatalogKpis(options?: {
  includeStockAlerts?: boolean;
}): Promise<CatalogKpiSnapshot> {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return { ...EMPTY };

  const branchId = useSessionStore.getState().currentBranchId ?? null;
  return computeKpisFromRemoteSnapshot(branchId, options);
}
